/**
 * @file CustomTable.tsx
 * @module app/custom/data-table/CustomTable
 * @description Provides the CustomTable reusable table component or expansion behavior for presenting structured operational data.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.3.0
 */

/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { HTMLProps, useState } from "react";
import {
    ColumnDef,
    ExpandedState,
    flexRender,
    getCoreRowModel,
    getExpandedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    useReactTable,
} from "@tanstack/react-table";
import clsx from "clsx";
import "./index.css";
import AnimatedExpand from "./AnimatedExpand";
import Loader from "../../../Global/loader";

interface CustomTableProps<T = any> {
    data: T[];
    columns: ColumnDef<T>[];
    getSubRows?: (row: T) => any[] | undefined;
    title?: string;
    className?: string;
    enablePagination?: boolean;
    pageSize?: number;
    enableFiltering?: boolean;
    enableExpanding?: boolean; // New flag to control expansion
    onRowClick?: (row: T) => void;
    hideHeader?: boolean;
    loading?: boolean;
}

function IndeterminateCheckbox({
    indeterminate,
    className = "",
    ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
    const ref = React.useRef<HTMLInputElement>(null!);

    React.useEffect(() => {
        if (typeof indeterminate === "boolean") {
            ref.current.indeterminate = !rest.checked && indeterminate;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ref, indeterminate]);

    return (
        <input
            type="checkbox"
            ref={ref}
            className={className + " cursor-pointer"}
            {...rest}
        />
    );
}

function Filter({ column, table }: { column: any; table: any }) {
    const firstValue = table
        .getPreFilteredRowModel()
        .flatRows[0]?.getValue(column.id);

    const columnFilterValue = column.getFilterValue();

    return typeof firstValue === "number" ? (
        <div className="d-flex gap-2">
            <input
                type="number"
                value={(columnFilterValue as [number, number])?.[0] ?? ""}
                onChange={(e) =>
                    column.setFilterValue((old: [number, number]) => [
                        e.target.value,
                        old?.[1],
                    ])
                }
                placeholder="Min"
                className="form-control form-control-sm"
                style={{ width: "80px" }}
            />
            <input
                type="number"
                value={(columnFilterValue as [number, number])?.[1] ?? ""}
                onChange={(e) =>
                    column.setFilterValue((old: [number, number]) => [
                        old?.[0],
                        e.target.value,
                    ])
                }
                placeholder="Max"
                className="form-control form-control-sm"
                style={{ width: "80px" }}
            />
        </div>
    ) : (
        <input
            type="text"
            value={(columnFilterValue ?? "") as string}
            onChange={(e) => column.setFilterValue(e.target.value)}
            placeholder="Search..."
            className="form-control form-control-sm"
            style={{ width: "150px" }}
        />
    );
}

export function CustomTable<T = any>({
    data,
    columns,
    getSubRows,
    // title,
    className,
    enablePagination = false,
    pageSize = 10,
    enableFiltering = false,
    enableExpanding = false, // Default to true for backward compatibility
    onRowClick,
    hideHeader = false,
    loading = false,
}: CustomTableProps<T>) {
    const [expanded, setExpanded] = useState<ExpandedState>({});

    const table = useReactTable({
        data,
        columns: columns as any,
        state: {
            expanded: enableExpanding ? expanded : {}, // Only manage expanded state if enabled
        },
        onExpandedChange: enableExpanding ? setExpanded : undefined,
        getSubRows: enableExpanding ? (getSubRows as any) : undefined,
        getRowCanExpand: enableExpanding ? undefined : () => false, // Disable expansion when flag is false
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: enablePagination
            ? getPaginationRowModel()
            : undefined,
        getFilteredRowModel: enableFiltering ? getFilteredRowModel() : undefined,
        getExpandedRowModel: enableExpanding ? getExpandedRowModel() : undefined, // Only enable expanded row model if needed
        initialState: {
            pagination: {
                pageSize,
            },
        },
    });

    const paginationRange = (
        current: number,
        total: number,
        maxPages: number = 5
    ) => {
        let delta = Math.floor(maxPages / 2);
        let range: (number | string)[] = [];
        let left = Math.max(1, current - delta);
        let right = Math.min(total, current + delta);

        if (right - left + 1 < maxPages) {
            if (left === 1) {
                right = Math.min(total, left + maxPages - 1);
            } else if (right === total) {
                left = Math.max(1, right - maxPages + 1);
            }
        }

        for (let i = left; i <= right; i++) {
            range.push(i);
        }

        if (left > 2) {
            range.unshift("...");
        }
        if (left > 1) {
            range.unshift(1);
        }

        if (right < total - 1) {
            range.push("...");
        }
        if (right < total) {
            range.push(total);
        }

        return range;
    };

    const currentPage = table.getState().pagination.pageIndex + 1;
    const totalPages = table.getPageCount();
    const pages = paginationRange(currentPage, totalPages, 5);

    return (
        <div className={className}>
            <div className="table-responsive">
                <table
                    className="table table-row-gray-300 align-middle gs-0 gy-4"
                    style={{ borderCollapse: "collapse", marginBottom: "0rem" }}
                >
                    {!hideHeader &&
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id} className="fw-bold text-muted" style={{ borderBottom: "1px solid #dee2e6" }}>
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            className="p-6 fs-16 fw-600 text-dark align-middle"
                                            style={{ width: header.getSize() }}
                                        >
                                            {header.isPlaceholder ? null : (
                                                <div>
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                    {enableFiltering && header.column.getCanFilter() ? (
                                                        <div className="mt-2">
                                                            <Filter column={header.column} table={table} />
                                                        </div>
                                                    ) : null}
                                                </div>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                    }
                    <tbody>
                        {loading ?
                            <tr>
                                <td colSpan={columns.length} className="text-center py-5">
                                    <div className="d-flex justify-content-center align-items-center">
                                        <Loader loading={loading} />
                                    </div>
                                </td>
                            </tr>
                            : table.getRowModel().rows.length > 0 ? (
                                table
                                    .getRowModel()
                                    .rows.map((row, rowIndex, rowsArray) => {
                                        const isLastRow = rowIndex === rowsArray.length - 1;
                                        return (
                                            <React.Fragment key={row.id}>
                                                <tr
                                                    className={clsx(row.depth > 0 && "table-active", onRowClick && "table-row-clickable")}
                                                    onClick={() => onRowClick?.(row.original)}
                                                    style={{ borderBottom: isLastRow ? "none" : "1px solid #dee2e6", cursor: onRowClick ? "pointer" : "default" }}
                                                >
                                                    {row.getVisibleCells().map((cell) => (
                                                        <td
                                                            key={cell.id}
                                                            className={`px-6 indented-cell-depth-${row.depth} fs-15 fw-600`}
                                                            style={{ borderBottom: isLastRow ? "none" : "1px solid #dee2e6" }}
                                                        >
                                                            {flexRender(
                                                                cell.column.columnDef.cell,
                                                                cell.getContext()
                                                            )}
                                                        </td>
                                                    ))}
                                                </tr>

                                                {/* Only render expanded content if expanding is enabled */}
                                                {enableExpanding && row.getIsExpanded() && (
                                                    <AnimatedExpand expanded={row.getIsExpanded()}>
                                                        <div
                                                            style={{ paddingLeft: `${(row.depth + 1) * 1.5}rem` }}
                                                        ></div>
                                                    </AnimatedExpand>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                            ) : (
                                <tr>
                                    <td colSpan={columns.length} className="text-center">
                                        <span className="fs-14 fw-bold">No data found</span>
                                    </td>
                                </tr>
                            )}
                    </tbody>
                </table>
            </div>

            {enablePagination && (
                <div className="row d-flex flex-wrap justify-content-between my-5 px-6">
                    <div className="col-auto">
                        <select
                            className="form-select form-select-sm"
                            style={{ width: "auto" }}
                            value={table.getState().pagination.pageSize}
                            onChange={(e) => {
                                table.setPageSize(Number(e.target.value));
                            }}
                        >
                            {[10, 20, 30].map((pageSize) => (
                                <option key={pageSize} value={pageSize}>
                                    {pageSize}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div
                        className="col d-flex align-items-center justify-content-end"
                        style={{ zIndex: 0 }}
                    >
                        <ul className="pagination">
                            <li
                                className={clsx(
                                    "page-item previous",
                                    !table.getCanPreviousPage() ? "disabled" : ""
                                )}
                            >
                                <a
                                    href="#"
                                    className="page-link"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        table.previousPage();
                                    }}
                                >
                                    <i className="previous"></i>
                                </a>
                            </li>

                            {pages.map((pageNum, idx) =>
                                pageNum === "..." ? (
                                    <li key={idx} className="page-item disabled">
                                        <span className="page-link">...</span>
                                    </li>
                                ) : (
                                    <li
                                        key={pageNum}
                                        className={clsx(
                                            "page-item",
                                            pageNum === currentPage ? "active" : ""
                                        )}
                                    >
                                        <a
                                            href="#"
                                            className={clsx(
                                                "page-link",
                                                pageNum === currentPage ? "active" : ""
                                            )}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                table.setPageIndex((pageNum as number) - 1);
                                            }}
                                        >
                                            {pageNum}
                                        </a>
                                    </li>
                                )
                            )}

                            <li
                                className={clsx(
                                    "page-item next",
                                    !table.getCanNextPage() ? "disabled" : ""
                                )}
                            >
                                <a
                                    href="#"
                                    className="page-link"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        table.nextPage();
                                    }}
                                >
                                    <i className="next"></i>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
}

export { IndeterminateCheckbox };
